import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Pencil, Plus, Save, Trash2, Upload, X } from "lucide-react";
import * as adminApi from "@/api/admin";
import type { NewsItem } from "@/api/admin";

export function NewsView() {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // Link field disabled — news opens on its own page instead.
  // const [link, setLink] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [adding, setAdding] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  // Edit mode: the form above is reused for editing an existing item.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState('');
  const [removeImage, setRemoveImage] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = () => {
    setLoading(true);
    adminApi.getNews()
      .then(setItems)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setTitle(''); setDescription(''); /* setLink(''); */ setImageFile(null);
    setEditingId(null); setExistingImageUrl(''); setRemoveImage(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const startEdit = (item: NewsItem) => {
    setEditingId(item.id);
    setTitle(item.title);
    setDescription(item.description);
    setExistingImageUrl(item.image_url);
    setImageFile(null);
    setRemoveImage(false);
    setConfirmDeleteId(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setAdding(true);
    try {
      if (editingId !== null) {
        await adminApi.updateNews(editingId, { title, description, image: imageFile, removeImage });
      } else {
        await adminApi.createNews({ title, description, link: '', image: imageFile });
      }
      resetForm();
      load();
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    await adminApi.deleteNews(id);
    if (editingId === id) resetForm();
    setDeletingId(null);
    setConfirmDeleteId(null);
    load();
  };

  const imagePreview = imageFile
    ? URL.createObjectURL(imageFile)
    : (editingId !== null && !removeImage && existingImageUrl) || null;

  return (
    <div className="space-y-8">
      <div
        ref={formRef}
        className={`bg-white border rounded-xl p-6 space-y-5 scroll-mt-6 ${editingId !== null ? 'border-[#00d9d9]' : 'border-gray-200'}`}
      >
        <div>
          <h2 className="text-base font-semibold text-gray-900">{editingId !== null ? 'Edit News Item' : 'Add News Item'}</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {editingId !== null
              ? 'Change the details below and click Save.'
              : 'Fill in the details below and click Add to publish.'}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-gray-600">Title <span className="text-red-400">*</span></label>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="News title" required />
            </div>
            {/* Link field disabled — news opens on its own page instead.
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-gray-600">Link URL</label>
              <Input value={link} onChange={e => setLink(e.target.value)} placeholder="https://..." type="url" />
            </div>
            */}
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-600">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Short description..."
              rows={3}
              className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-[#00d9d9]/30 focus:border-[#00d9d9]"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-600">Image</label>
            <div className="flex items-center gap-4">
              {imagePreview && (
                <img src={imagePreview} alt="preview" className="w-16 h-16 object-cover rounded-lg border border-gray-200 shrink-0" />
              )}
              <label className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:border-[#00d9d9] hover:text-[#00d9d9] cursor-pointer transition-colors">
                <Upload size={14} />
                {imageFile ? imageFile.name : imagePreview ? 'Replace image' : 'Choose image'}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => { setImageFile(e.target.files?.[0] ?? null); setRemoveImage(false); }}
                />
              </label>
              {imageFile ? (
                <button
                  type="button"
                  onClick={() => { setImageFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                  title="Clear selected file"
                >
                  <X size={14} />
                </button>
              ) : editingId !== null && existingImageUrl && !removeImage ? (
                <button
                  type="button"
                  onClick={() => setRemoveImage(true)}
                  className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                >
                  Remove image
                </button>
              ) : editingId !== null && removeImage ? (
                <button
                  type="button"
                  onClick={() => setRemoveImage(false)}
                  className="text-xs text-gray-400 hover:text-[#00d9d9] transition-colors"
                >
                  Undo remove
                </button>
              ) : null}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            {editingId !== null && (
              <button
                type="button"
                onClick={resetForm}
                disabled={adding}
                className="px-4 h-8 rounded-md border border-gray-200 bg-white text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            )}
            <Button type="submit" disabled={adding || !title.trim()} size="sm" className="bg-[#00d9d9] text-black hover:bg-[#00c0c1] px-6">
              {adding ? (
                <Loader2 size={14} className="animate-spin mr-1.5" />
              ) : editingId !== null ? (
                <Save size={14} className="mr-1.5" />
              ) : (
                <Plus size={14} className="mr-1.5" />
              )}
              {editingId !== null ? 'Save Changes' : 'Add Item'}
            </Button>
          </div>
        </form>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Published Items ({items.length})</h3>
        {loading ? (
          <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}</div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 border border-dashed border-gray-200 rounded-xl text-gray-400">
            <p className="text-sm">No news items yet.</p>
            <p className="text-xs mt-1">Add your first item above.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(item => (
              <div key={item.id} className={`bg-white border rounded-xl overflow-hidden flex flex-col transition-colors ${editingId === item.id ? 'border-[#00d9d9]' : 'border-gray-200 hover:border-gray-300'}`}>
                {item.image_url ? (
                  <img src={item.image_url} alt={item.title} className="w-full h-40 object-cover" />
                ) : (
                  <div className="w-full h-40 bg-gray-50 flex items-center justify-center text-gray-300 text-xs">No image</div>
                )}
                <div className="flex-1 p-4 flex flex-col gap-1.5">
                  <p className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug">{item.title}</p>
                  {item.description && <p className="text-xs text-gray-500 line-clamp-3 leading-relaxed">{item.description}</p>}
                  {/* Link display disabled.
                  {item.link && (
                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-xs text-[#00d9d9] hover:underline truncate">{item.link}</a>
                  )}
                  */}
                  <div className="mt-auto pt-2 flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-400">{new Date(item.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    {confirmDeleteId === item.id ? (
                      <div className="flex gap-2 items-center">
                        <span className="text-xs text-gray-500">Delete?</span>
                        <button onClick={() => handleDelete(item.id)} className="text-xs text-red-500 font-medium hover:underline">
                          {deletingId === item.id ? <Loader2 size={12} className="animate-spin" /> : 'Yes'}
                        </button>
                        <button onClick={() => setConfirmDeleteId(null)} className="text-xs text-gray-400 hover:underline">No</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button onClick={() => startEdit(item)} className="text-gray-300 hover:text-[#00d9d9] transition-colors p-1" title="Edit">
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => setConfirmDeleteId(item.id)} className="text-gray-300 hover:text-red-500 transition-colors p-1" title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
